import { FaSearch } from "react-icons/fa";
import { FiRefreshCcw } from "react-icons/fi";
import styled from "styled-components";
import { designTokens } from "../../components/ui/foundation/DesignTokens";

export const PanelContainer = styled.div`
  width: 100%;
  height: 100%;
`;

// Filters Section
export const FiltersCard = styled.div`
  background: white;
  border: 1px solid ${designTokens.colors.gray[200]};
  border-radius: ${designTokens.radius.lg};
  padding: ${designTokens.spacing.xl};
  margin-bottom: ${designTokens.spacing.lg};
`;

export const FiltersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${designTokens.spacing.lg};
  flex-wrap: wrap;
  gap: ${designTokens.spacing.md};

  h3 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
    color: ${designTokens.colors.gray[900]};
    font-family: ${designTokens.fonts.heading};
  }
`;

export const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${designTokens.spacing.md};
  align-items: flex-end;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 140px;

  @media (max-width: 900px) {
    width: 100%;
  }
`;

export const FilterLabel = styled.label`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${designTokens.colors.gray[500]};
  font-family: ${designTokens.fonts.heading};
`;

export const CalendarSection = styled.section`
  display: flex;
  align-items: center;
  width: 100%;
  gap: ${designTokens.spacing.md};
  flex-wrap: wrap;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: stretch;
  }

  .MuiFormControl-marginNormal {
    margin: 0;
  }

  .MuiInputBase-root {
    font-size: 0.9rem;
    border-radius: ${designTokens.radius.md};
  }

  .MuiInputLabel-root {
    font-size: 0.85rem;
    color: ${designTokens.colors.gray[500]};
  }
`;

export const ButtonActions = styled.div`
  display: flex;
  gap: ${designTokens.spacing.sm};
`;

export const SearchIcon = styled(FaSearch)``;
export const RefreshIcon = styled(FiRefreshCcw)``;

export const LoaderContainer = styled.div`
  width: 100%;
  height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Legacy components for backwards compatibility
export const ImportButtonContainer = styled.div`
  width: 10.75rem;
  height: 2.75rem;
`;

export const ImportIconContainer = styled.div`
  width: 24px;
  height: 24px;
  margin-right: 10px;
`;

export const ImportIcon = styled.img`
  width: 100%;
`;

export const ExportIconContainer = styled.div`
  width: 24px;
  height: 24px;
  margin-right: 10px;
`;

export const ExportIcon = styled.img`
  width: 100%;
`;

// Design System Table Components
export const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.9rem;

  @media (max-width: 900px) {
    font-size: 0.8rem;
  }
`;

export const Th = styled.th`
  text-align: left;
  padding: 14px 20px;
  background: ${designTokens.colors.gray[50]};
  color: ${designTokens.colors.gray[500]};
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid ${designTokens.colors.gray[200]};
  white-space: nowrap;

  @media (max-width: 900px) {
    padding: 12px 14px;
    font-size: 0.7rem;
  }
`;

export const Td = styled.td`
  padding: 14px 20px;
  border-bottom: 1px solid ${designTokens.colors.gray[100]};
  color: ${designTokens.colors.gray[700]};
  vertical-align: middle;
  font-size: 0.85rem;

  @media (max-width: 900px) {
    padding: 12px 14px;
    font-size: 0.8rem;
  }
`;

export const Tr = styled.tr`
  transition: background-color 0.15s ease;
  background-color: white;

  &:hover {
    background: #F9FAFF;
  }

  ${(props) => props.selected && `background-color: ${designTokens.colors.primary[50]};`}
`;

export const MoreInfoButton = styled.button`
  background: ${designTokens.colors.gray[100]};
  color: ${designTokens.colors.gray[700]};
  border: 1px solid ${designTokens.colors.gray[200]};
  padding: 6px 12px;
  border-radius: 99px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: 0.2s;
  font-family: ${designTokens.fonts.heading};

  &:hover {
    background: white;
    border-color: ${designTokens.colors.gray[300]};
  }
`;

// Tag Badge for displaying tags
export const TagBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.75rem;
  background: ${designTokens.colors.gray[100]};
  color: ${designTokens.colors.gray[600]};
  border: 1px solid ${designTokens.colors.gray[200]};
  margin-right: 4px;
  margin-bottom: 4px;
`;

// Empty state for table
export const TableEmpty = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${designTokens.spacing['3xl']};
  text-align: center;
  color: ${designTokens.colors.gray[500]};
`;

export const TableEmptyIcon = styled.div`
  font-size: 3rem;
  margin-bottom: ${designTokens.spacing.md};
  opacity: 0.5;
`;

export const TableEmptyText = styled.p`
  font-size: 1rem;
  color: ${designTokens.colors.gray[500]};
  margin: 0;
`;

// Pagination Styles
export const TableFooter = styled.div`
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid ${designTokens.colors.gray[100]};
  flex-wrap: wrap;
  gap: ${designTokens.spacing.md};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const TableSummary = styled.div`
  font-size: 0.85rem;
  color: ${designTokens.colors.gray[500]};
`;

export const PaginationContainer = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

export const PaginationText = styled.span`
  font-size: 0.85rem;
  color: ${designTokens.colors.gray[400]};
  margin: 0 8px;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: ${designTokens.colors.gray[600]};
  }

  ${(props) => props.active && `
    color: ${designTokens.colors.primary[600]};
    font-weight: 600;
  `}

  ${(props) => props.disabled && `
    opacity: 0.5;
    cursor: not-allowed;
  `}
`;

export const PageBtn = styled.button`
  min-width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid ${designTokens.colors.gray[200]};
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${designTokens.colors.gray[600]};
  font-size: 0.8rem;
  transition: 0.2s;
  font-family: ${designTokens.fonts.body};

  &:hover:not(:disabled) {
    background: ${designTokens.colors.gray[50]};
  }

  ${(props) => props.active && `
    background: ${designTokens.colors.primary[500]};
    color: white;
    border-color: ${designTokens.colors.primary[500]};

    &:hover {
      background: ${designTokens.colors.primary[600]};
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Platform Icon Container
export const PlatformIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

// Agent Cell with Avatar
export const AgentCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const AgentAvatar = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${(props) => props.bgColor || designTokens.colors.primary[100]};
  color: ${(props) => props.textColor || designTokens.colors.primary[600]};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  flex-shrink: 0;
`;
